import { useCallback, useState } from "react";
import { ApiError, type MessageCode } from "@/lib/api/error";

/**
 * Collects server-side validation errors from an {@link ApiError} so a form can
 * render them inline next to the relevant input.
 *
 * Stores message *codes* (not translated strings) keyed by the backend field
 * name, so the displayed text re-translates if the locale changes. Pair with the
 * `<FieldErrors>` component (`@/components/ui/field-error`).
 */
export function useApiFieldErrors() {
  const [fieldErrors, setFieldErrors] = useState<Record<string, MessageCode[]>>(
    {},
  );
  const [generalErrors, setGeneralErrors] = useState<MessageCode[]>([]);

  /**
   * Populate field/general errors from a caught error. Returns `true` if the
   * error was an {@link ApiError} carrying any field- or form-level errors.
   * Non-`ApiError` values (and errors without field info) are ignored — those
   * are surfaced by the global toast instead.
   */
  const captureError = useCallback((error: unknown): boolean => {
    if (!(error instanceof ApiError)) return false;
    const fields: Record<string, MessageCode[]> = {};
    for (const [field, errs] of Object.entries(error.invalidFields)) {
      fields[field] = errs.map((e) => e.code);
    }
    const general = error.generalErrors.map((e) => e.code);
    setFieldErrors(fields);
    setGeneralErrors(general);
    return (
      Object.keys(fields).length > 0 || general.length > 0
    );
  }, []);

  const clear = useCallback(() => {
    setFieldErrors({});
    setGeneralErrors([]);
  }, []);

  /** Set a client-side validation error for a single field. */
  const setFieldError = useCallback((name: string, codes: MessageCode[]) => {
    setFieldErrors((prev) => ({ ...prev, [name]: codes }));
  }, []);

  const clearField = useCallback((name: string) => {
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  return { fieldErrors, generalErrors, captureError, clear, clearField, setFieldError };
}
